import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, MapPin, Building2, User as UserIcon, LogOut,
} from "lucide-react";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const items = [
    { to: "/app", end: true, icon: LayoutDashboard, label: t("nav.dashboard"), testid: "nav-dashboard" },
    { to: "/app/query", icon: MapPin, label: t("nav.query"), testid: "nav-query" },
    { to: "/app/sites", icon: Building2, label: t("nav.sites"), testid: "nav-sites" },
    { to: "/app/profile", icon: UserIcon, label: t("nav.profile"), testid: "nav-profile" },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F9F8F6]" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-[#E5E2D9] bg-[#F1EFE7] flex md:flex-col">
        <div className="p-5 hidden md:block">
          <Logo />
        </div>
        <nav className="flex-1 px-2 md:px-3 py-2 md:py-4 flex md:flex-col gap-1 overflow-x-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-[#1A2F24] text-white"
                    : "text-[#1A2F24] hover:bg-[#E5E2D9]"
                }`
              }
            >
              <it.icon size={18} />
              <span className="hidden md:inline">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:block p-3 border-t border-[#E5E2D9]">
          <div className="mb-2 px-2 flex flex-col gap-1">
            <span className="text-xs text-[#5C6B61] truncate">{user?.email}</span>
            <span className="self-start text-[10px] uppercase tracking-wide bg-[#1A2F24] text-white px-2 py-0.5 rounded-full">
              {user?.subscription_plan}
            </span>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#1A2F24] hover:bg-[#E5E2D9] rounded-md transition-colors"
          >
            <LogOut size={16} /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Top bar for mobile + content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden border-b border-[#E5E2D9] bg-[#F1EFE7] px-4 py-3 flex items-center justify-between">
          <Logo small />
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button
              data-testid="logout-button-mobile"
              onClick={async () => { await logout(); navigate("/"); }}
              className="p-2 rounded-md hover:bg-[#E5E2D9]"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <header className="hidden md:flex border-b border-[#E5E2D9] bg-white/60 backdrop-blur px-6 py-3 items-center justify-end gap-3">
          <LanguageToggle />
        </header>
        <main className="flex-1 p-6 md:p-8" data-testid="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
